import { useNVKEventSubscribe, LocalStorage, SEARCH_PARAMS, getCorrectedURLSearchParams, useEventSubscribe } from '@novakid/frontend-core';
import { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { captureException } from '@sentry/browser';
import { matchPath, RouterProvider } from 'react-router-dom';
import { IUser } from '@novakid/ts-types';

import { getSignUpToken, getSignUpUrl, getUserRegion, injectBloomreach } from './utils';
import { UserService, ScreenLoaderService } from './services';
import { CREDENTIAL_PARAMS, SPECIAL_PAGES } from './consts';
import { withGeoHandling } from './hocs/withGeoHandling';
import { getUser, getRegions, logout } from './api';
import { withRedirects } from './withRedirects';
import { ScreenLoader } from './components';
import { useAppNavigation } from './hooks';
import { UserContext } from './contexts';
import { router } from './router';

const LazyError = lazy(() => import('./components/Error'));

declare global {
  interface Window {
    dataLayer?: Array<{ event: string }>;
  }
}

export const REGIONAL_DOMAINS = [
  'https://school.novakid.pl',
  'https://school.novakid.de',
  'https://school.novakid.it',
  'https://school.novakid.es',
  'https://school.novakid.ru',
  'https://school.novakid.com.tr',
  'https://school.novakid.net.cn',
];

export const App = withRedirects(
  withGeoHandling(() => {
    const [isRemoteLoading, setRemoteLoading] = useState(false);
    const [user, setUser] = useState<IUser | null>(null);
    const [isLoading, setLoading] = useState(true);
    const [hasError, setError] = useState(false);

    const fetchUser = useCallback((id: number, token: string) => {
      getUser(id, token)
        .then((user) => {
          LocalStorage.setNVKItem('NVK_USER_EMAIL', user.email);
          LocalStorage.setNVKItem('NVK_AUTH_TOKEN', token);
          LocalStorage.setNVKItem('NVK_USER_ID', id);
          LocalStorage.setNVKItem('NVK_USER', user);
          // For backward compatibility only
          LocalStorage.setItem('authToken', token);
          LocalStorage.setItem('userId', id);

          if (user.group === 'users' && !user.phone) {
            const signUpToken = getSignUpToken(user, token);

            getRegions()
              .then((regions) => getSignUpUrl({ region: getUserRegion(regions, user.region_code) }))
              .catch(() => getSignUpUrl({ region: getUserRegion([], user.region_code) }))
              .then((signUpUrl) => {
                window.location.href = `${signUpUrl}/phone_submission?token=${signUpToken}`;
              });

            return;
          }

          if (user.group === 'teachers') {
            window.dataLayer?.push({ event: 'teacher-initialised' });
          }

          if (user.group === 'users') {
            window.dataLayer?.push({ event: 'parent-initialised' });
          }

          setUser(user);

          const searchParams = getCorrectedURLSearchParams(router.state.location.search);
          const pathname = router.state.location.pathname;

          const returnUrl = searchParams.get(SEARCH_PARAMS.RETURN_URL);

          if (returnUrl && !returnUrl.includes('localhost')) {
            let url = decodeURIComponent(returnUrl);

            REGIONAL_DOMAINS.forEach((domain) => {
              if (url.startsWith(domain)) {
                url = url.replace(domain, 'https://school.novakidschool.com');
              }
            });

            window.location.replace(url);
            return Promise.resolve();
          }

          if (returnUrl && returnUrl.includes('localhost') && !pathname.startsWith('/select-account')) {
            const devUrl = new URL(returnUrl);

            devUrl.searchParams.set(SEARCH_PARAMS.AUTH_TOKEN, token);
            devUrl.searchParams.set(SEARCH_PARAMS.USER_ID, id.toString());

            window.location.replace(devUrl);
            return;
          }

          Object.values(CREDENTIAL_PARAMS).forEach((param) => {
            searchParams.delete(param);
          });

          const isSignIn = pathname.startsWith('/signin');

          router.navigate(
            {
              pathname: isSignIn ? '/' : pathname,
              search: searchParams.toString(),
            },
            { replace: true }
          );

          setLoading(false);
        })
        .catch((err) => {
          if (err.status >= 500) {
            captureException(err);
            setLoading(false);
            setError(true);
            return;
          }

          const searchParams = getCorrectedURLSearchParams(router.state.location.search);
          const pathname = router.state.location.pathname;

          const hasReturnPath = searchParams.has(SEARCH_PARAMS.RETURN_PATH);
          const hasReturnUrl = searchParams.has(SEARCH_PARAMS.RETURN_URL);
          const isSignInEntryPoint = pathname.startsWith('/signin');
          const isRootEntryPoint = pathname === '/';

          if (!isSignInEntryPoint && !isRootEntryPoint && !hasReturnPath && !hasReturnUrl) {
            searchParams.set(SEARCH_PARAMS.RETURN_PATH, pathname);
          }

          router.navigate(
            {
              search: searchParams.toString(),
              pathname: '/signin',
            },
            { replace: true }
          );

          setLoading(false);
        });
    }, []);

    const onUserUpdated = useCallback((user: IUser) => {
      LocalStorage.setNVKItem('NVK_USER', user);
      setUser(user);
    }, []);

    const onLogout = useCallback(() => {
      const token = LocalStorage.getNVKItem('NVK_AUTH_TOKEN') as string;
      LocalStorage.removeNVKItem('NVK_AUTH_TOKEN');
      LocalStorage.removeNVKItem('NVK_USER');
      LocalStorage.removeItem('authToken');
      setUser(null);

      logout(token).catch(() => {
        // do nothing
      });

      router.navigate({
        search: router.state.location.search,
        pathname: '/signin',
      });
    }, []);

    useEffect(() => {
      const location = router.state.location;
      const isSpecialPage = SPECIAL_PAGES.some((path) => matchPath(path, location.pathname));
      const isSignIn = location.pathname.startsWith('/signin');
      const isEmptySearch = location.search === '';

      if (isSignIn && isEmptySearch) {
        setLoading(false);
        return;
      }

      if (isSpecialPage) {
        setLoading(false);
        return;
      }

      if (UserService.hasUserCredentials) {
        fetchUser(UserService.userId as number, UserService.authToken as string);
        return;
      }

      setLoading(false);
    }, [fetchUser]);

    useEffect(() => {
      const searchParams = new URLSearchParams(router.state.location.search);

      if (searchParams.has('sendSentryTestLog')) {
        const logMessage = searchParams.get('sendSentryTestLog');
        captureException(new Error(`CONTAINER APP LOG: ${logMessage}`));

        setTimeout(() => {
          console.log('send timeout test exception');
          captureException(new Error(`CONTAINER APP LOG AFTER 10 sec: ${logMessage}`));
        }, 10000);
      }
    }, []);

    useEffect(() => {
      if (user && window.gtag) {
        window.gtag('set', { user_id: user.id });
      }

      const isRuRegion = LocalStorage.getNVKItem('NVK_GEO_REGION') === 'RU';

      if (user && isRuRegion) {
        injectBloomreach(user);
      }
    }, [user]);

    const onStartRemoteAppLoading = useCallback(() => setRemoteLoading(true), []);
    const onEndRemoteAppLoding = useCallback(() => setRemoteLoading(false), []);

    useEventSubscribe(ScreenLoaderService.CONTAINER_APP_BOUNCER_START, onStartRemoteAppLoading);
    useEventSubscribe(ScreenLoaderService.CONTAINER_APP_BOUNCER_STOP, onEndRemoteAppLoding);
    useNVKEventSubscribe('NVK_USER_UPDATED', onUserUpdated);
    useNVKEventSubscribe('NVK_USER_RECIEVED', setUser);
    useNVKEventSubscribe('NVK_LOGOUT', onLogout);
    useAppNavigation();

    if (hasError) {
      return (
        <Suspense fallback={<ScreenLoader />}>
          <LazyError />
        </Suspense>
      );
    }

    if (isLoading) {
      return <ScreenLoader />;
    }

    return (
      <>
        {isRemoteLoading && <ScreenLoader />}
        <UserContext.Provider value={user}>
          <RouterProvider router={router} />
        </UserContext.Provider>
      </>
    );
  })
);
